import React, {FC, useEffect, useMemo, useRef, useState} from "react";
import {Badge, Button, Col, Container, FormCheck, FormSelect, InputGroup, Row, Tab, Tabs} from "react-bootstrap";
import {
    RRReportItemsType,
    RRReportItemType,
    RRReportType
} from "../../../reports/ReportComputing";
import {MaybeDate} from "../../../../types/Maybe";
import {useAppSelector} from "../../../../hooks";
import {
    dateToString,
    dateWithMaxTime,
    dateWithZeroTime,
    getDatesInPeriod,
    minutesToHoursAsShortString,
    showErrorsListInToast,
    zeroToEmptyStringAsCurrency,
} from "../../../../tools";
import ReactDatePicker from "react-datepicker";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCalendar, faClock, faFileExcel, faInfo, faInfoCircle, faMoneyBill} from "@fortawesome/free-solid-svg-icons";
import RRTable from "../../../../components/rr-table/RRTable";
import CenteredColumn from "../../../../components/CenteredColumn";
import {collection, getDocs, orderBy, query, where} from "firebase/firestore";
import {appFirestore} from "../../../../../index";
import {RREmployeesType, RREmployeeType} from "../../../../types/RREmployeeType";
import {employeesDataConverter} from "../../../../../firebase/FirebaseEmployeeLoader";
import {RREventsType, RREventType} from "../../../../types/RREventType";
import {selectAllEvents} from "../../../../../features/AppDataSlice";
import {selectCurrentEmployee} from "../../../../../features/AppStateSlice";
import {utils, writeFile} from "xlsx";
import {getReportMonthOptions} from "../../../reports/reportMonthOptions";
import calculateLogisticsWorkReport, {LOGISTICS_NO_EVENT_EVENT_KEY} from "./LogisticsWorkReportComputing";
import {stringAsDepartment} from "../../../../types/RRDepartmentType";

type DescriptionItem = {
    name: string;
    minutes: number;
    price: number;
}

function parseDescriptionItems(description: string): DescriptionItem[] {
    try {
        const parsed = JSON.parse(description) as DescriptionItem[];
        return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
        return description ? [{name: description, minutes: 0, price: 0}] : [];
    }
}

function hasReportData(item: RRReportItemType): boolean {
    return item.minutes + item.minutesAsSupervisor + item.minutesAsHoliday > 0 || item.price > 0;
}

function ItemDescriptionRenderer(props: { item: string }) {
    const descriptionItems = parseDescriptionItems(props.item);
    if (descriptionItems.length > 0) {
        return (
            <>
                {
                    descriptionItems.map((item, idx) => (
                        <div key={`${item.name}_${idx}`} className={"rr-table-detail-line"} title={item.name}>
                            {item.name}
                            {item.minutes > 0 && ` [${minutesToHoursAsShortString(item.minutes)}]`}
                        </div>
                    ))
                }
            </>
        );
    }

    return <div>{props.item}</div>;
}

function ReportCellTime(props: { item: RRReportItemType, isTotal?: boolean, hideBreakdownDetails?: boolean }) {
    const [showDetails, setShowDetails] = useState(false);
    const item = props.item;
    const total = item.minutesAsHoliday + item.minutesAsSupervisor + item.minutes;
    const hasDetailDescription = showDetails && Boolean(item.description);

    return (
        <Row className={"m-1" + (hasDetailDescription ? " rr-table-detail-cell-content" : "")}>
            {
                total > 0 &&
                <Col
                    xs={"12"}
                    className={"p-1 border border-secondary rounded text-nowrap cursor-pointer" +
                        ((total > 1440 && props.isTotal === false) ? " bg-danger text-light" : "")}
                    onClick={() => setShowDetails(!showDetails)}
                >
                    <FontAwesomeIcon icon={faClock}/>&nbsp;
                    {minutesToHoursAsShortString(total)}
                    {
                        item.description &&
                        <FontAwesomeIcon icon={faInfo} className={"float-end me-2 text-info"}/>
                    }
                </Col>
            }
            {
                showDetails && <>
                    {
                        !props.hideBreakdownDetails &&
                        item.minutesAsSupervisor > 0 &&
                        <Col xs={"12"} className={"p-1 ps-2 text-nowrap"}>
                            <Badge>СУП</Badge>&nbsp;
                            {minutesToHoursAsShortString(item.minutesAsSupervisor)}
                        </Col>
                    }
                    {
                        !props.hideBreakdownDetails &&
                        item.minutesAsHoliday > 0 &&
                        <Col xs={"12"} className={"p-1 ps-2 text-nowrap"}>
                            <Badge bg={"success"}>ПОЧ</Badge>&nbsp;
                            {minutesToHoursAsShortString(item.minutesAsHoliday)}
                        </Col>
                    }
                    {
                        !props.hideBreakdownDetails &&
                        item.minutes > 0 &&
                        <Col xs={"12"} className={"p-1 ps-2 text-nowrap"}>
                            <Badge bg={"secondary"}>ТРАН</Badge>&nbsp;
                            {minutesToHoursAsShortString(item.minutes)}
                        </Col>
                    }
                    {
                        item.description &&
                        <Col xs={"12"} className={"p-1 ps-2"}>
                            <ItemDescriptionRenderer item={item.description}/>
                        </Col>
                    }
                </>
            }
        </Row>
    );
}

function ReportCellPrice(props: { item: RRReportItemType }) {
    const currentEmployee = useAppSelector(selectCurrentEmployee);
    const canSeePayment = currentEmployee?.isSuperAdmin || currentEmployee?.canSeePayment;

    return (
        <Row className={"m-1"}>
            {
                canSeePayment &&
                <Col xs={"12"} className={"p-1 border border-secondary rounded text-nowrap"}>
                    <FontAwesomeIcon icon={faMoneyBill}/> {zeroToEmptyStringAsCurrency(props.item.price, "")}
                </Col>
            }
        </Row>
    );
}

function EmployeeRow(props: {
    dates: string[],
    report: RRReportType | undefined,
    employee: RREmployeeType,
    mode: string,
    asSubRows?: boolean
}) {
    const report = props.report;
    const employee = props.employee;
    const total: RRReportItemType = {
        minutes: 0,
        minutesAsSupervisor: 0,
        minutesAsHoliday: 0,
        price: 0,
        date: "",
    };
    const descriptionItems: DescriptionItem[] = [];
    const currentEmployee = useAppSelector(selectCurrentEmployee);

    props.dates.forEach(date => {
        const item = report?.report.employees.find(value => value.employee.id === employee.id && value.date === date);
        if (!item) {
            return;
        }

        total.price += item.price;
        total.minutes += item.minutes;
        total.minutesAsSupervisor += item.minutesAsSupervisor;
        total.minutesAsHoliday += item.minutesAsHoliday;

        if (item.description) {
            descriptionItems.push(...parseDescriptionItems(item.description));
        }
    });

    if (descriptionItems.length > 0) {
        total.description = JSON.stringify(descriptionItems);
    }

    const backgroundStyle = props.asSubRows
        ? {backgroundColor: "white", width: "75px"}
        : {backgroundColor: "antiquewhite", width: "75px"};

    if (!hasReportData(total)) {
        return null;
    }

    return (
        <tr>
            <th className={"min-width-200px lh-2"} style={backgroundStyle}>
                {employee.name}
            </th>
            {
                props.mode === "FULL" &&
                props.dates.map(date => {
                    const item = report?.report.employees.find(value => value.employee.id === employee.id && value.date === date);
                    if (!item) {
                        return (
                            <React.Fragment key={`${date}_${employee.id}_empty`}>
                                <td/>
                                <td/>
                            </React.Fragment>
                        );
                    }

                    return (
                        <React.Fragment key={`${date}_${employee.id}`}>
                            <td>
                                {currentEmployee?.isSuperAdmin ? <ReportCellTime item={item} isTotal={false}/> : ""}
                            </td>
                            <td><ReportCellPrice item={item}/></td>
                        </React.Fragment>
                    );
                })
            }
            <th style={backgroundStyle}>
                <ReportCellTime item={total}/>
            </th>
            <th style={backgroundStyle}>
                <ReportCellPrice item={total}/>
            </th>
        </tr>
    );
}

function getTransportEventName(event: RREventType): string {
    if (event.eventNumber === LOGISTICS_NO_EVENT_EVENT_KEY) {
        return "БЕЗ СЪБИТИЕ";
    }

    if (event.eventNumber === "WAREHOUSE") {
        return "СКЛАД";
    }

    return `[ ${event.eventNumber} ] ${event.eventName}`;
}

function EventRow(props: {
    dates: string[],
    report: RRReportType | undefined,
    event: RREventType,
    mode: string,
    asHeader: boolean,
}) {
    const total: RRReportItemType = {
        minutes: 0,
        minutesAsSupervisor: 0,
        minutesAsHoliday: 0,
        price: 0,
        date: "",
    };
    const descriptionItems: DescriptionItem[] = [];
    const cellBackgroundStyle = props.asHeader
        ? {backgroundColor: "antiquewhite", width: "75px"}
        : {width: "75px"};
    const hideBreakdownDetails = props.event.eventNumber === LOGISTICS_NO_EVENT_EVENT_KEY;

    props.dates.forEach(date => {
        const item = props.report?.report.events.find(
            value => value.event.eventNumber === props.event.eventNumber && value.date === date
        );
        if (!item) {
            return;
        }

        total.price += item.price;
        total.minutes += item.minutes;
        total.minutesAsSupervisor += item.minutesAsSupervisor;
        total.minutesAsHoliday += item.minutesAsHoliday;

        if (item.description) {
            descriptionItems.push(...parseDescriptionItems(item.description));
        }
    });

    if (descriptionItems.length > 0) {
        total.description = JSON.stringify(descriptionItems);
    }

    if (!hasReportData(total)) {
        return null;
    }

    return (
        <tr>
            <th className={"min-width-200px"} style={{backgroundColor: "antiquewhite"}}>
                {getTransportEventName(props.event)}
            </th>
            {
                props.mode === "FULL" &&
                props.dates.map(date => {
                    const item = props.report?.report.events.find(
                        value => value.event.eventNumber === props.event.eventNumber && value.date === date
                    );
                    if (!item) {
                        return (
                            <React.Fragment key={`${date}_${props.event.eventNumber}_empty`}>
                                <td style={cellBackgroundStyle}/>
                                <td style={cellBackgroundStyle}/>
                            </React.Fragment>
                        );
                    }

                    return (
                        <React.Fragment key={`${date}_${props.event.eventNumber}`}>
                            <td style={cellBackgroundStyle}>
                                <ReportCellTime item={item} hideBreakdownDetails={hideBreakdownDetails}/>
                            </td>
                            <td style={cellBackgroundStyle}>
                                <ReportCellPrice item={item}/>
                            </td>
                        </React.Fragment>
                    );
                })
            }
            <th style={{backgroundColor: "antiquewhite"}}>
                <ReportCellTime item={total} hideBreakdownDetails={hideBreakdownDetails}/>
            </th>
            <th style={{backgroundColor: "antiquewhite"}}>
                <ReportCellPrice item={total}/>
            </th>
        </tr>
    );
}

function TotalRow(props: {
    dates: string[],
    report: RRReportType | undefined,
    mode: string,
    viewMode: string,
}) {
    const totals: RRReportItemsType = [];
    const total: RRReportItemType = {
        minutes: 0,
        minutesAsSupervisor: 0,
        minutesAsHoliday: 0,
        price: 0,
        date: "",
    };

    const sourceItems = props.viewMode === "EVENT"
        ? props.report?.report.events ?? []
        : props.report?.report.employees ?? [];

    sourceItems.forEach(item => {
        let dailyTotal = totals.find(value => value.date === item.date);
        if (!dailyTotal) {
            dailyTotal = {
                date: item.date,
                minutes: 0,
                minutesAsSupervisor: 0,
                minutesAsHoliday: 0,
                price: 0,
            };
            totals.push(dailyTotal);
        }

        dailyTotal.price += item.price;
        dailyTotal.minutes += item.minutes;
        dailyTotal.minutesAsSupervisor += item.minutesAsSupervisor;
        dailyTotal.minutesAsHoliday += item.minutesAsHoliday;

        total.price += item.price;
        total.minutes += item.minutes;
        total.minutesAsSupervisor += item.minutesAsSupervisor;
        total.minutesAsHoliday += item.minutesAsHoliday;
    });

    if (!hasReportData(total)) {
        return null;
    }

    return (
        <tr>
            <th className={"min-width-200px lh-2em"} style={{backgroundColor: "antiquewhite"}}>
                ВСИЧКО
            </th>
            {
                props.mode === "FULL" &&
                props.dates.map(date => {
                    const item = totals.find(value => value.date === date);
                    if (!item) {
                        return (
                            <React.Fragment key={`total_${date}_empty`}>
                                <th style={{backgroundColor: "antiquewhite"}}/>
                                <th style={{backgroundColor: "antiquewhite"}}/>
                            </React.Fragment>
                        );
                    }

                    return (
                        <React.Fragment key={`total_${date}`}>
                            <th style={{backgroundColor: "antiquewhite"}}>
                                <ReportCellTime item={item}/>
                            </th>
                            <th style={{backgroundColor: "antiquewhite"}}>
                                <ReportCellPrice item={item}/>
                            </th>
                        </React.Fragment>
                    );
                })
            }
            <th style={{backgroundColor: "antiquewhite"}}>
                <ReportCellTime item={total}/>
            </th>
            <th style={{backgroundColor: "antiquewhite"}}>
                <ReportCellPrice item={total}/>
            </th>
        </tr>
    );
}

function sortTransportEvents(events: RREventsType): RREventsType {
    return [...events].sort((left, right) => {
        if (left.eventNumber === LOGISTICS_NO_EVENT_EVENT_KEY && right.eventNumber !== LOGISTICS_NO_EVENT_EVENT_KEY) {
            return right.eventNumber === "WAREHOUSE" ? -1 : 1;
        }
        if (right.eventNumber === LOGISTICS_NO_EVENT_EVENT_KEY && left.eventNumber !== LOGISTICS_NO_EVENT_EVENT_KEY) {
            return left.eventNumber === "WAREHOUSE" ? 1 : -1;
        }
        if (left.eventNumber === "WAREHOUSE" && right.eventNumber !== "WAREHOUSE") {
            return 1;
        }
        if (right.eventNumber === "WAREHOUSE" && left.eventNumber !== "WAREHOUSE") {
            return -1;
        }

        const leftNumber = Number(left.eventNumber);
        const rightNumber = Number(right.eventNumber);
        const leftIsNumeric = !Number.isNaN(leftNumber);
        const rightIsNumeric = !Number.isNaN(rightNumber);

        if (leftIsNumeric && rightIsNumeric) {
            return leftNumber - rightNumber;
        }

        return left.eventNumber.localeCompare(right.eventNumber, "bg-BG");
    });
}

const LogisticsWorkReportView: FC = () => {
    const today = dateWithZeroTime(new Date())!;
    const monthOptions = useMemo(() => getReportMonthOptions(today), [today]);
    const defaultMonthOption = monthOptions[1] ?? monthOptions[0];

    const [working, setWorking] = useState(false);
    const [withEmployees, setWithEmployees] = useState(false);
    const [periodFrom, setPeriodFrom] = useState<MaybeDate>();
    const [periodTo, setPeriodTo] = useState<MaybeDate>();
    const [selectedMonth, setSelectedMonth] = useState<string>(defaultMonthOption.value);
    const [dpPeriodFrom, setDpPeriodFrom] = useState<MaybeDate>(defaultMonthOption.periodFrom);
    const [dpPeriodTo, setDpPeriodTo] = useState<MaybeDate>(defaultMonthOption.periodTo);
    const [holidayDates, setHolidayDates] = useState<string[] | undefined>();
    const [report, setReport] = useState<RRReportType | undefined>(undefined);
    const [employees, setEmployees] = useState<RREmployeesType | undefined>(undefined);
    const [viewMode, setViewMode] = useState("EVENT");
    const [viewDetails, setViewDetails] = useState("SHORT");
    const currentEmployee = useAppSelector(selectCurrentEmployee);
    const allEvents = useAppSelector(selectAllEvents);
    const employeeId = currentEmployee?.isAdmin ? undefined : currentEmployee?.id;
    const tableRef = useRef<HTMLTableElement>(null);

    const events = useMemo(() => {
        if (!report) {
            return [] as RREventsType;
        }

        const uniqueEvents = new Map<string, RREventType>();
        report.report.events.forEach(item => {
            if (!uniqueEvents.has(item.event.eventNumber)) {
                uniqueEvents.set(item.event.eventNumber, item.event);
            }
        });

        return sortTransportEvents(Array.from(uniqueEvents.values()));
    }, [report]);

    useEffect(() => {
        setHolidayDates(undefined);
        setEmployees(undefined);
        setReport(undefined);

        if (!periodFrom || !periodTo) {
            return;
        }

        const filterPeriodFrom = periodFrom;
        const filterPeriodTo = periodTo;
        let isCancelled = false;

        async function loadFiltersData() {
            try {
                const holidaysPromise = getDocs(query(
                    collection(appFirestore, "bankHolidays"),
                    where("dateObject", ">=", filterPeriodFrom),
                    where("dateObject", "<=", filterPeriodTo),
                    where("department", "==", stringAsDepartment("logistics"))
                ));

                const employeesPromise = getDocs(query(
                    collection(appFirestore, "employees"),
                    where("isDeleted", "==", false),
                    where("isEmployee", "==", true),
                    where("isLogistics", "==", true),
                    orderBy("listIndex_logistics", "asc")
                ));

                const [holidaySnapshot, employeeSnapshot] = await Promise.all([holidaysPromise, employeesPromise]);
                if (isCancelled) {
                    return;
                }

                setHolidayDates(holidaySnapshot.docs.map(doc => doc.data().date));
                setEmployees(employeesDataConverter(employeeSnapshot.docs));
            } catch (error) {
                if (isCancelled) {
                    return;
                }

                const errorMessage = error instanceof Error ? error.message : "Неуспешно зареждане на филтрите.";
                showErrorsListInToast("Възникна грешка", [errorMessage]);
                setHolidayDates([]);
                setEmployees([]);
            }
        }

        void loadFiltersData();

        return () => {
            isCancelled = true;
        };
    }, [periodFrom, periodTo]);

    useEffect(() => {
        if (!periodFrom || !periodTo || !holidayDates || !employees) {
            setWorking(false);
            return;
        }

        const reportPeriodFrom = periodFrom;
        const reportPeriodTo = periodTo;
        const reportHolidayDates = holidayDates;
        const reportEmployees = employees;
        let isCancelled = false;
        setWorking(true);

        async function buildReport() {
            try {
                const result = await calculateLogisticsWorkReport(
                    reportPeriodFrom,
                    reportPeriodTo,
                    reportEmployees,
                    reportHolidayDates,
                    allEvents.data,
                    employeeId
                );

                if (!isCancelled) {
                    setReport(result);
                }
            } catch (error) {
                if (isCancelled) {
                    return;
                }

                const errorMessage = error instanceof Error ? error.message : "Неуспешно изчисляване на справката.";
                showErrorsListInToast("Възникна грешка", [errorMessage]);
                setReport(undefined);
            } finally {
                if (!isCancelled) {
                    setWorking(false);
                }
            }
        }

        void buildReport();

        return () => {
            isCancelled = true;
        };
    }, [periodFrom, periodTo, holidayDates, employees, allEvents.data, employeeId]);

    function setRefPeriod() {
        setPeriodFrom(dateWithZeroTime(dpPeriodFrom));
        setPeriodTo(dateWithMaxTime(dpPeriodTo));
    }

    const dates = useMemo(() => {
        if (!periodFrom || !periodTo) {
            return [] as string[];
        }

        return getDatesInPeriod(periodFrom, periodTo).map(date => dateToString(date));
    }, [periodFrom, periodTo]);

    function applyMonthSelection(optionValue: string) {
        const selectedOption = monthOptions.find(option => option.value === optionValue);
        if (!selectedOption) {
            return;
        }

        setSelectedMonth(selectedOption.value);
        setDpPeriodFrom(selectedOption.periodFrom);
        setDpPeriodTo(selectedOption.periodTo);
    }

    function exportExcel(): void {
        if (!tableRef.current) {
            return;
        }

        const workbook = utils.table_to_book(tableRef.current);
        writeFile(workbook, "Report.xlsx");
    }

    return <Container fluid>
        <h1>Справки и отчети</h1>
        <h3>Изработени часове</h3>

        <Row className={"g-2 mt-3 flex-nowrap"}>
            <Col>
                <Row className={"lh-2-5em flex-nowrap"}>
                    <Col xs={"auto"}>
                        <Row>
                            <Col xs={"auto"} className={"lh-2-5em"}>
                                Месец:
                            </Col>
                            <Col xs={"auto"} className={"d-inline-block"}>
                                <FormSelect
                                    className={"mb-2"}
                                    disabled={working}
                                    value={selectedMonth}
                                    onChange={event => applyMonthSelection(event.target.value)}
                                >
                                    {
                                        monthOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))
                                    }
                                </FormSelect>
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={"auto"}>
                        <Row>
                            <Col xs={"auto"} className={"lh-2-5em"}>
                                Начална дата:
                            </Col>
                            <Col xs={"auto"} className={"d-inline-block"}>
                                <InputGroup className="mb-2 w-180px flex-nowrap">
                                    <ReactDatePicker
                                        disabled={working}
                                        id={"transportReportPeriodFromDate"}
                                        className={"form-control flex-nowrap"}
                                        selected={dpPeriodFrom}
                                        onChange={date => setDpPeriodFrom(date || defaultMonthOption.periodFrom)}
                                        value={dateToString(dpPeriodFrom)}
                                        maxDate={dpPeriodTo}
                                    />
                                    <label htmlFor={"transportReportPeriodFromDate"}>
                                        <InputGroup.Text className={"lh-2-5em"}>
                                            <FontAwesomeIcon icon={faCalendar} size={"xl"}/>
                                        </InputGroup.Text>
                                    </label>
                                </InputGroup>
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={"auto"}>
                        <Row>
                            <Col xs={"auto"} className={"lh-2-5em"}>
                                Крайна дата:
                            </Col>
                            <Col xs={"auto"} className={"d-inline-block"}>
                                <InputGroup className="mb-2 w-180px flex-nowrap">
                                    <ReactDatePicker
                                        disabled={working}
                                        id={"transportReportPeriodToDate"}
                                        className={"form-control flex-nowrap"}
                                        selected={dpPeriodTo}
                                        onChange={date => setDpPeriodTo(date || today)}
                                        value={dateToString(dpPeriodTo)}
                                        minDate={dpPeriodFrom}
                                    />
                                    <label htmlFor={"transportReportPeriodToDate"}>
                                        <InputGroup.Text className={"lh-2-5em"}>
                                            <FontAwesomeIcon icon={faCalendar} size={"xl"}/>
                                        </InputGroup.Text>
                                    </label>
                                </InputGroup>
                            </Col>
                        </Row>
                    </Col>
                    <Col>
                        {
                            !working && <>
                                <Button onClick={setRefPeriod}>Изчисли</Button>&nbsp;
                                <Button onClick={() => exportExcel()} variant={"outline-success"}>
                                    <FontAwesomeIcon icon={faFileExcel}/>
                                </Button>&nbsp;
                            </>
                        }
                    </Col>
                </Row>
            </Col>
        </Row>

        {
            report &&
            <Row className={"g-2 mt-3"}>
                <Col xs={"12"}>
                    <Tabs activeKey={viewMode} onSelect={eventKey => setViewMode(eventKey || "EVENT")}>
                        <Tab title={"Отчет по събития"} eventKey={"EVENT"}/>
                        {currentEmployee?.isSuperAdmin && <Tab title={"Отчет по служители"} eventKey={"EMPL"}/>}
                    </Tabs>
                </Col>
            </Row>
        }

        {
            report &&
            <Row className={"g-2 mt-3"}>
                <Col xs={"auto"}>
                    <Tabs
                        activeKey={viewDetails}
                        variant={"pills"}
                        onSelect={eventKey => setViewDetails(eventKey || "SHORT")}
                    >
                        <Tab title={"Обобщена за периода"} eventKey={"SHORT"}/>
                        <Tab title={"По дни за периода"} eventKey={"FULL"}/>
                    </Tabs>
                </Col>
                <Col className={"ms-2 fw-bold"}>
                    {
                        viewMode === "EVENT" && currentEmployee?.isSuperAdmin &&
                        <FormCheck
                            checked={withEmployees}
                            onChange={event => setWithEmployees(event.target.checked)}
                            label={"Покажи и служителите"}
                        />
                    }
                </Col>
                <Col className={"fw-bold text-end"}>
                    <FontAwesomeIcon icon={faInfoCircle}/>&nbsp;Натиснете полето с времето, за да видите
                    разпределението му по групи.
                </Col>
            </Row>
        }

        <Row className={"g-2 mt-3"}>
            <Col xs={viewDetails === "FULL" ? "12" : "auto"}>
                {
                    report ?
                        <RRTable
                            ref={tableRef}
                            autoHeight={true}
                            bottomOffset={48}
                            tableClassName={"rr-table-auto-width"}
                        >
                            <thead>
                            <tr>
                                <CenteredColumn header colSpan={dates.length * 2 + 3}>
                                    <div className={"m-2 fw-bold"}>
                                        {viewMode === "EMPL" ? "Отчет по служители " : "Отчет по събития"} за периода
                                        от {dateToString(periodFrom)} до {dateToString(periodTo)}
                                    </div>
                                </CenteredColumn>
                            </tr>
                            <tr>
                                <th colSpan={dates.length * 2 + 3} className={"bg-light"}></th>
                            </tr>
                            <tr>
                                <th className={"w-160px"}>Служител / събитие</th>
                                {
                                    viewDetails === "FULL" &&
                                    dates.map(date => (
                                        <CenteredColumn header={true} key={date} colSpan={2}>
                                            {date}
                                            {
                                                holidayDates?.includes(date) &&
                                                <span className={"float-end"}>
                                                    <Badge>H</Badge>
                                                </span>
                                            }
                                        </CenteredColumn>
                                    ))
                                }
                                <CenteredColumn header={true} className={"w-100px"} colSpan={2}>Всичко</CenteredColumn>
                            </tr>
                            </thead>

                            <tbody>
                            {
                                viewMode === "EMPL" &&
                                employees?.map(employee => (
                                    <EmployeeRow
                                        key={employee.id}
                                        dates={dates}
                                        employee={employee}
                                        mode={viewDetails}
                                        report={report}
                                    />
                                ))
                            }
                            {
                                viewMode === "EVENT" &&
                                events.flatMap(event => {
                                    const rows: React.ReactNode[] = [
                                        <EventRow
                                            key={`event_${event.eventNumber}`}
                                            dates={dates}
                                            event={event}
                                            mode={viewDetails}
                                            asHeader={withEmployees}
                                            report={report}
                                        />
                                    ];

                                    if (withEmployees) {
                                        const eventReport: RRReportType = {
                                            periodFrom: report.periodFrom,
                                            periodTo: report.periodTo,
                                            report: {
                                                events: [],
                                                employees: [],
                                            }
                                        };

                                        report.report.events.forEach(item => {
                                            if (item.event.eventNumber === event.eventNumber) {
                                                eventReport.report.employees.push(...item.employees);
                                            }
                                        });

                                        employees?.forEach(employee => {
                                            rows.push(
                                                <EmployeeRow
                                                    key={`${event.eventNumber}_${employee.id}`}
                                                    dates={dates}
                                                    employee={employee}
                                                    mode={viewDetails}
                                                    asSubRows
                                                    report={eventReport}
                                                />
                                            );
                                        });
                                    }

                                    return rows;
                                })
                            }
                            <TotalRow
                                dates={dates}
                                report={report}
                                mode={viewDetails}
                                viewMode={viewMode}
                            />
                            </tbody>
                        </RRTable>
                        : <strong>Моля, изберете период и натиснете "Изчисли"...</strong>
                }
            </Col>
        </Row>
    </Container>;
}

export default LogisticsWorkReportView;
